/**
 * WorkspaceProvider — creates one `WorkspaceStore` for a composition and binds
 * a `FileSource` to it: seed at boot, persist edits, react to external changes.
 *
 * Persistence has two independent halves, wired per source:
 *   - write-back  → for a MUTABLE source (has `write`): each dirty file is
 *     debounced-written back, then marked saved (so `base` catches up).
 *   - overlay     → when `overlayKey` is set: the working set is mirrored to
 *     localStorage. This is how a READONLY source (a public repo) keeps your
 *     local experiments across reloads, layered on its pristine base.
 *
 * Seeding handles both adapter shapes: a sync `load()` (localStorage) is used as
 * the store's initial value (no flash); an async `load()` (remote/server) starts
 * on `fallback`, then applies the resolved snapshot — but only if the user
 * hasn't already edited, so a slow load never clobbers typing.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  createWorkspaceStore,
  type WorkspaceStore,
  type WorkspaceStoreOptions,
} from "./WorkspaceStore.js";
import type { TabSpec } from "./tabs.js";
import type {
  FileSource,
  WorkspaceSnapshot,
} from "./filesource/types.js";
import type { Vfs } from "@archlang/viewer-core";
import { WorkspaceContext } from "./WorkspaceContext.js";
import { usePanelKindResolver } from "./plugins/PluginRegistryContext.js";
import { WorkbenchHostProvider } from "./plugins/WorkbenchHostProvider.js";
import { useWorkbenchHost } from "./plugins/hostContext.js";
import type { HostCapabilities } from "./plugins/hostContext.js";

export interface WorkspaceProviderProps {
  /** Where the files come from. Its `capabilities.readonly` flows to the store. */
  readonly source: FileSource;
  /** Seed used when the source loads nothing (e.g. the bundled demo). */
  readonly fallback?: WorkspaceSnapshot;
  /** Persist the working set to localStorage under this key (survives reload).
   *  For a readonly source these are local experiments over the pristine base. */
  readonly overlayKey?: string;
  readonly persistDebounceMs?: number;
  /** Extra store options — view-state seeds (initialSelection / initialMode /
   *  initialViewport / initialAspect) and persistView. Provider-owned fields
   *  (file seeding, readonly, overlay persistence) always win. */
  readonly storeOptions?: Omit<
    WorkspaceStoreOptions,
    "initial" | "readonly" | "persist" | "persistDebounceMs"
  >;
  /** Use a caller-built store (tests, Studio host). `source` is then for binders
   *  only; the store's own state is whatever the caller seeded. */
  readonly store?: WorkspaceStore;
  readonly children: ReactNode;
}

export function WorkspaceProvider(props: WorkspaceProviderProps): ReactNode {
  const {
    source,
    fallback,
    overlayKey,
    persistDebounceMs,
    storeOptions,
    store: externalStore,
    children,
  } = props;

  // Pending async snapshot to apply after mount (set during lazy init).
  const pendingSeed = useRef<Promise<WorkspaceSnapshot | null> | null>(null);

  // Custom-tab kind resolver from the plugin registry (empty in the viewer,
  // registry-backed under Studio's WorkbenchPluginHost). Captured once at store
  // creation — the registry map is stable for the mount's lifetime.
  const resolvePanelKind = usePanelKindResolver();

  const [store] = useState<WorkspaceStore>(() => {
    if (externalStore) return externalStore;

    const loaded = source.load();
    const s = createWorkspaceStore({
      ...storeOptions,
      readonly: source.capabilities.readonly,
      persist: overlayKey ? (snap) => writeOverlay(overlayKey, snap) : undefined,
      persistDebounceMs,
      panelKinds: resolvePanelKind,
    });

    if (loaded instanceof Promise) {
      pendingSeed.current = loaded; // applied in the seed effect below
    } else {
      seed(s, loaded ?? fallback ?? null, overlayKey);
    }
    return s;
  });

  // An EXTERNAL store (Studio creates its own, outside the plugin host at store
  // construction) doesn't get `panelKinds` at creation, so install the registry
  // resolver here — this provider sits inside the host, so `resolvePanelKind` is
  // registry-backed. Custom-tab openTab dedup + persistence then work.
  useEffect(() => {
    if (externalStore) store.setPanelKindResolver(resolvePanelKind);
  }, [externalStore, store, resolvePanelKind]);

  // Apply an async load once it resolves (untouched guard inside `seed`). The
  // ref is NOT consumed here: under StrictMode the effect runs setup→cleanup→
  // setup, and nulling it on the first (cancelled) run would leave the second
  // run with nothing to seed. Each run attaches its own cancellation guard; the
  // surviving run's `.then` seeds (and `seed` is idempotent — it no-ops once the
  // workspace is non-empty).
  useEffect(() => {
    const p = pendingSeed.current;
    if (!p) return;
    let cancelled = false;
    void p
      .then((snap) => {
        if (!cancelled) seed(store, snap ?? fallback ?? null, overlayKey);
      })
      .catch(() => {
        if (!cancelled) seed(store, fallback ?? null, overlayKey);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Write-back binder: a mutable source persists mutations, routing each to the
  // matching granular method (so a rename reaches a server as a rename, keeping
  // identity/history — not remove + write). Content EDITS are debounced; the
  // discrete create/remove/rename fire promptly. `markSaved` then advances base.
  // Pending edits flush synchronously on pagehide so a fast reload/close never
  // drops the last edit (React cleanup doesn't run on navigation).
  //
  // A source implementing `writeEdits` takes the span-anchored route instead:
  // offsets are only valid against the exact text they were computed on, so a
  // debounce that coalesces two batches would splice the second at positions
  // the first already moved. Those batches go out promptly and in order; the
  // debounce stays for keystroke-rate whole-content writes.
  useEffect(() => {
    if (source.capabilities.readonly) return;
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const pendingEdit = new Map<string, string>();
    const delay = persistDebounceMs ?? 400;
    // Serialises span-anchored batches (one global chain — cross-file ordering
    // is harmless and one chain is simpler than a map of them): each batch
    // must land against the text the previous write produced, so they can
    // never overlap in flight.
    let editChain: Promise<unknown> = Promise.resolve();

    /**
     * A source can refuse a write — a host that lost its buffer, a file
     * deleted underneath, a denied quota. Swallowing that silently is bad;
     * letting it reject is worse. The batches below share ONE promise chain,
     * so an uncaught rejection makes the chain permanently rejected: every
     * later write is skipped, for every file, with nothing to show for it but
     * an unhandled rejection. The path simply stays dirty, which is the truth.
     */
    const refused = (path: string) => (err: unknown) => {
      console.warn(`archlang: the host refused a write to ${path}`, err);
    };

    const cancel = (path: string): void => {
      const t = timers.get(path);
      if (t) clearTimeout(t);
      timers.delete(path);
      pendingEdit.delete(path);
    };
    const flushEdit = (path: string): void => {
      const content = pendingEdit.get(path);
      cancel(path);
      if (content === undefined) return;
      void Promise.resolve(source.write?.(path, content))
        .then(() => store.markSaved([path]))
        .catch(refused(path));
    };

    const off = store.on((ev) => {
      switch (ev.type) {
        case "fileChanged": {
          if (source.writeEdits && ev.edits && ev.edits.length > 0) {
            // A pending debounced whole-content write holds the very text
            // this batch's offsets were computed ON — text the host has NOT
            // seen yet (it is still inside the debounce). Flush it through
            // the same chain FIRST, then splice: dropping it would apply the
            // offsets against an older host buffer and corrupt the file.
            const held = pendingEdit.get(ev.path);
            cancel(ev.path);
            const edits = ev.edits;
            editChain = editChain
              .then(async () => {
                if (held !== undefined) await source.write?.(ev.path, held);
                return source.writeEdits?.(ev.path, edits);
              })
              .then(() => store.markSaved([ev.path]))
              .catch(refused(ev.path));
            break;
          }
          pendingEdit.set(ev.path, ev.content);
          const existing = timers.get(ev.path);
          if (existing) clearTimeout(existing);
          timers.set(ev.path, setTimeout(() => flushEdit(ev.path), delay));
          break;
        }
        case "fileCreated":
          void Promise.resolve(
            (source.create ?? source.write)?.(ev.path, ev.content),
          )
            .then(() => store.markSaved([ev.path]))
            .catch(refused(ev.path));
          break;
        case "fileRemoved":
          cancel(ev.path);
          void Promise.resolve(source.remove?.(ev.path))
            .then(() => store.markSaved([ev.path]))
            .catch(refused(ev.path));
          break;
        case "fileRenamed": {
          cancel(ev.from);
          const done = () => store.markSaved([ev.from, ev.to]);
          if (source.rename) {
            // Rename to keep identity, THEN write so any edit made in the same
            // breath (whose pending debounce we just cancelled) isn't lost to the
            // source's stale on-disk content.
            void Promise.resolve(source.rename(ev.from, ev.to))
              .then(() => source.write?.(ev.to, ev.content))
              .then(done)
              .catch(refused(ev.to));
          } else {
            void Promise.resolve(source.remove?.(ev.from))
              .then(() => (source.create ?? source.write)?.(ev.to, ev.content))
              .then(done)
              .catch(refused(ev.to));
          }
          break;
        }
        // "reveal" is not persistence — ignored here.
      }
    });

    const flushAll = (): void => {
      for (const path of [...pendingEdit.keys()]) flushEdit(path);
    };
    window.addEventListener("pagehide", flushAll);

    return () => {
      off();
      window.removeEventListener("pagehide", flushAll);
      flushAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, source]);

  // Watch binder: external changes (server push / collab) apply to base AND
  // working via `applyExternal`, which emits no event — so the write-back binder
  // above can't echo them straight back out (the collab feedback loop).
  useEffect(() => {
    if (!source.watch) return;
    return source.watch((change) => {
      if (store.getState().dirtyPaths.has(change.path)) return; // keep local edit
      store.applyExternal(change.path, change.content);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, source]);

  // Flush the overlay persist on page hide (fast close/reload doesn't drop the
  // last experiment), and release the timer on a real unmount — but never
  // destroy a caller-owned (externalStore) store.
  useEffect(() => {
    const onHide = () => store.flush();
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      if (!externalStore) store.destroy();
    };
  }, [store, externalStore]);

  // The store-backed host capabilities: the actionable surface plugin
  // contributions (custom tabs, nav sections, commands) call through. `openTab`
  // is what `ctx.openTab({ kind: "custom", … })` reaches; `openTabs` is the live
  // read. This provider EXTENDS the outer ctx (same pattern as the command-
  // palette provider): it forwards the outer's reads/actions (e.g. Studio's
  // `activeRepo` + `toast`, supplied by the route above) and overrides only the
  // store-backed ones — so a contribution rendered inside this subtree (the
  // board's context menu) still sees the outer `activeRepo`, rather than this
  // provider SHADOWING it to null.
  const outer = useWorkbenchHost();
  const hostCapabilities = useMemo<HostCapabilities>(
    () => ({
      activeRepo: () => outer.activeRepo,
      selection: () => outer.selection,
      model: () => outer.model(),
      revealInNav: (ref) => outer.revealInNav(ref),
      runCommand: (id) => outer.runCommand(id),
      toast: (msg) => outer.toast(msg),
      openTab: (spec) => void store.openTab(spec as TabSpec),
      openTabs: () =>
        store.getState().tabs.map((t) => ({ id: t.id, kind: t.kind })),
    }),
    [store, outer],
  );

  return (
    <WorkspaceContext.Provider value={store}>
      <WorkbenchHostProvider capabilities={hostCapabilities}>
        {children}
      </WorkbenchHostProvider>
    </WorkspaceContext.Provider>
  );
}

// Seed the store: base = source content, working = local overlay (if any) over
// that base. Skips if the user already touched the workspace (async race guard).
function seed(
  store: WorkspaceStore,
  snapshot: WorkspaceSnapshot | null,
  overlayKey: string | undefined,
): void {
  if (!snapshot) return;
  if (store.getState().files.size > 0) return; // already touched / seeded
  const overlay = overlayKey ? readOverlay(overlayKey) : null;
  store.hydrate({
    base: snapshot.files,
    working: overlay?.files,
    activeFile: overlay?.activeFile ?? snapshot.activeFile,
    assets: snapshot.assets,
    openTabs: overlay?.openTabs,
    // Keep any view-persisted tab list (board/doc/process tabs + order seeded
    // via `initialTabs`); just reconcile it to the freshly-loaded files.
    preserveTabs: true,
  });
}

// --- localStorage overlay (working-set mirror) -----------------------------

interface OverlayBlob {
  readonly files: Record<string, string>;
  readonly activeFile: string | null;
  readonly openTabs?: readonly string[];
}

function hasStorage(): boolean {
  return typeof window !== "undefined" && !!window.localStorage;
}

function readOverlay(key: string): WorkspaceSnapshot | null {
  if (!hasStorage()) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OverlayBlob> | null;
    if (!parsed?.files) return null;
    const files: Vfs = new Map(Object.entries(parsed.files));
    if (files.size === 0) return null;
    return { files, activeFile: parsed.activeFile ?? null, openTabs: parsed.openTabs };
  } catch {
    return null;
  }
}

function writeOverlay(key: string, snapshot: WorkspaceSnapshot): void {
  if (!hasStorage()) return;
  try {
    const blob: OverlayBlob = {
      files: Object.fromEntries(snapshot.files),
      activeFile: snapshot.activeFile,
      openTabs: snapshot.openTabs,
    };
    localStorage.setItem(key, JSON.stringify(blob));
  } catch {
    /* quota / private mode — skip */
  }
}
